import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { PageHeaderPluginActionsContext } from '@/components/ui/page-header-plugin-actions'

import { canvasPagePath, canvasesForEntity, canvasesForPage, getPluginSlots } from './slots'
import type { PluginCanvasEntity, PluginCanvasManifest } from './types'
import { PLUGIN_CANVAS_PARAM, useOpenPluginCanvas } from './use-open-plugin-canvas'

function LauncherButton({
  manifest,
  onOpen,
}: {
  manifest: PluginCanvasManifest
  onOpen: () => void
}) {
  return (
    <Button type="button" variant="outline" size="sm" title={manifest.description} onClick={onOpen}>
      {manifest.launcherLabel ?? manifest.title}
    </Button>
  )
}

/** Buttons for pack canvases registered against an entity kind. Renders nothing when none match. */
export function PluginCanvasLaunchers({ entity }: { entity: PluginCanvasEntity }) {
  const open = useOpenPluginCanvas()
  const manifests = canvasesForEntity(entity.kind, getPluginSlots().canvases)
  if (!manifests.length) return null
  return (
    <>
      {manifests.map((manifest) => (
        <LauncherButton key={manifest.id} manifest={manifest} onOpen={() => open(manifest.id, { entity })} />
      ))}
    </>
  )
}

/**
 * Puts launchers for canvases targeting the current page into its `PageHeader` actions,
 * and opens `?canvas=<id>` deep links. Wraps the page pane routes.
 */
export function PluginPageCanvasProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const open = useOpenPluginCanvas()
  const canvases = getPluginSlots().canvases
  const matches = useMemo(() => canvasesForPage(location.pathname, canvases), [canvases, location.pathname])
  const openedDeepLink = useRef<string | null>(null)

  const requested = new URLSearchParams(location.search).get(PLUGIN_CANVAS_PARAM)
  useEffect(() => {
    if (!requested) {
      openedDeepLink.current = null
      return
    }
    const key = `${location.pathname}?${requested}`
    if (openedDeepLink.current === key) return
    const match = matches.find((m) => m.manifest.id === requested)
    if (!match) return
    openedDeepLink.current = key
    open(match.manifest.id, { params: match.params })
    const search = new URLSearchParams(location.search)
    search.delete(PLUGIN_CANVAS_PARAM)
    const query = search.toString()
    navigate({ pathname: location.pathname, search: query ? `?${query}` : '' }, { replace: true })
  }, [location.pathname, location.search, matches, navigate, open, requested])

  const value = useMemo(() => {
    if (!matches.length) return null
    const launchers = matches.filter(({ manifest }) => manifest.launcher !== false)
    const glances = matches.filter(({ manifest }) => manifest.glance)
    return {
      actions: launchers.length ? (
        <>
          {launchers.map(({ manifest, params }) => (
            <LauncherButton key={manifest.id} manifest={manifest} onOpen={() => open(manifest.id, { params })} />
          ))}
        </>
      ) : null,
      below: glances.length ? (
        <>
          {glances.map(({ manifest, params }) => {
            const Glance = manifest.glance!
            return (
              <Glance
                key={manifest.id}
                canvasId={manifest.id}
                params={params}
                entity={null}
                pagePath={canvasPagePath(manifest, params)}
                open={() => open(manifest.id, { params })}
              />
            )
          })}
        </>
      ) : null,
    }
  }, [matches, open])

  return <PageHeaderPluginActionsContext.Provider value={value}>{children}</PageHeaderPluginActionsContext.Provider>
}
